import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import ManagerDashboard from "./pages/ManagerDashboard";
import DailyEntryForm from "./pages/DailyEntryForm";
import ScorecardView from "./pages/ScorecardView";
import ScorecardsListPage from "./pages/ScorecardsListPage";
import ExecutiveDashboard from "./pages/ExecutiveDashboard";
import RankingsPage from "./pages/RankingsPage";
import ComparisonPage from "./pages/ComparisonPage";
import LocationsPage from "./pages/LocationsPage";
import SettingsPage from "./pages/SettingsPage";
import TrendsPage from "./pages/TrendsPage";
import WeeklyReportPage from "./pages/WeeklyReportPage";
import BulkPaxImportPage from "./pages/BulkPaxImportPage";
import DailyScorecardPage from "./pages/DailyScorecardPage";
import BudgetManagementPage from "./pages/BudgetManagementPage";
import UserManagementPage from "./pages/UserManagementPage";
import AuditLogPage from "./pages/AuditLogPage";

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to={user?.role === 'executive' ? '/executive' : '/dashboard'} replace />;
  }
  
  return children;
};

// Public Route Component (redirects if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to={user?.role === 'executive' ? '/executive' : '/dashboard'} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      
      {/* Manager Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <ManagerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/entry" element={
        <ProtectedRoute>
          <DailyEntryForm />
        </ProtectedRoute>
      } />
      <Route path="/scorecards" element={
        <ProtectedRoute>
          <ScorecardsListPage />
        </ProtectedRoute>
      } />
      <Route path="/scorecards/:entryId" element={
        <ProtectedRoute>
          <ScorecardView />
        </ProtectedRoute>
      } />
      <Route path="/trends" element={
        <ProtectedRoute>
          <TrendsPage />
        </ProtectedRoute>
      } />
      <Route path="/daily-scorecard" element={
        <ProtectedRoute>
          <DailyScorecardPage />
        </ProtectedRoute>
      } />
      
      {/* Executive Routes */}
      <Route path="/executive" element={
        <ProtectedRoute requiredRole="executive">
          <ExecutiveDashboard />
        </ProtectedRoute>
      } />
      <Route path="/rankings" element={
        <ProtectedRoute requiredRole="executive">
          <RankingsPage />
        </ProtectedRoute>
      } />
      <Route path="/compare" element={
        <ProtectedRoute requiredRole="executive">
          <ComparisonPage />
        </ProtectedRoute>
      } />
      <Route path="/weekly-report" element={
        <ProtectedRoute requiredRole="executive">
          <WeeklyReportPage />
        </ProtectedRoute>
      } />
      <Route path="/pax-import" element={
        <ProtectedRoute requiredRole="executive">
          <BulkPaxImportPage />
        </ProtectedRoute>
      } />
      <Route path="/budgets" element={
        <ProtectedRoute requiredRole="executive">
          <BudgetManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute requiredRole="executive">
          <UserManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/audit-log" element={
        <ProtectedRoute requiredRole="executive">
          <AuditLogPage />
        </ProtectedRoute>
      } />
      <Route path="/locations" element={
        <ProtectedRoute requiredRole="executive">
          <LocationsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute requiredRole="executive">
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
